import { useCallback } from 'react';
import { useAuthStore } from '@stores/authStore.ts';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetMe } from '@hooks/useGetMe.ts';
import { SignInFormData } from '@features/user/auth';
import { signIn, signOut, signOutAdmin } from '@src/services/auth.ts';
import { TRole } from '@src/types';
import { toast } from 'react-toastify';
import { useCheckoutStore } from '@stores/checkoutStore.ts';
import { refreshCsrfToken } from '@packages/axios/index.ts';

export const useAuth = () => {
  const { auth } = useAuthStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { reset } = useCheckoutStore();
  const { refreshMe, me } = useGetMe();

  const handleLogin = useCallback(
    async (data: SignInFormData) => {
      try {
        const res = await signIn({ ...data, login: data.login });

        auth.setAccessToken(res.data.token);

        const me = await refreshMe();

        const fromPathname = (location.state as any)?.from?.pathname as
          | string
          | undefined;

        if (me?.role === TRole.Admin) {
          navigate(fromPathname?.startsWith('/admin') ? fromPathname : '/admin', {
            replace: true,
          });

          return;
        }

        if (fromPathname && !fromPathname.startsWith('/admin')) {
          navigate(fromPathname, { replace: true });

          return;
        }

        if (me?.complete_onboarding) {
          navigate('/', { replace: true });

          return;
        }
        navigate('/select-trading-platform', { replace: true });
      } catch (err: any) {
        if (err?.response?.data?.error_code === 'MUST_RESET_PASSWORD') {
          toast.warning(err.response.data.message);

          return;
        }
        toast.error(t('toast.failedToLogin'));
      }
    },
    [auth, location.state, navigate, refreshMe, t]
  );

  const logout = useCallback(async () => {
    const me = await refreshMe();

    reset();
    auth.reset();

    const isAdmin = me?.role === TRole.Admin;

    if (isAdmin) {
      await signOutAdmin();
    } else {
      await signOut();
    }

    // Backend deletes csrf_access_token cookie on logout,
    // so we must fetch a fresh CSRF token before navigating
    // to the sign-in page, otherwise the next login will fail.
    await refreshCsrfToken().catch(() => {});

    if (me?.role === TRole.Admin) {
      navigate('/admin/signin', { replace: true });

      return;
    }
    navigate('/signin', { replace: true });
  }, [auth, navigate, refreshMe, reset]);

  return {
    signIn: handleLogin,
    logout,
    me,
  };
};
